import * as z from 'zod';

export const CreateTicketReservationRequest = z.object({
  ticketReservation: z
    .object({
      _id: z
        .string()
        .describe('Ticket reservation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ticket reservation was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ticket reservation was updated.')
        .optional()
        .nullable(),
      expirationDate: z
        .date()
        .describe(
          "Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.\n\nWhen a reservation expires, tickets are released back to the available inventory.\nExpiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.\n\n**Note:** This field only applies to `PENDING` reservations."
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'PENDING',
          'CONFIRMED',
          'CANCELED',
          'CANCELED_MANUALLY',
          'EXPIRED',
        ])
        .optional(),
      memberId: z
        .string()
        .describe(
          'ID of the site member who created this reservation.\n\nOnly returned when the reservation was created by a logged-in site member.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      visitorId: z
        .string()
        .describe(
          'ID of the site visitor who created this reservation.\n\nReturned for both logged-in members and anonymous visitors.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      tickets: z
        .array(
          z.object({
            ticketLineItemId: z
              .string()
              .describe('Ticket line item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            eventId: z
              .string()
              .describe('ID of the event these tickets are for.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            ticketDefinitionId: z
              .string()
              .describe(
                'ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            quantity: z
              .number()
              .int()
              .describe('Number of tickets to reserve for this line item.')
              .min(1)
              .max(50),
            ticketInfo: z
              .object({
                seatId: z
                  .string()
                  .describe(
                    'Unique identifier for the assigned seat at the event venue.\n\nRequired for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                seatInfo: z
                  .object({
                    sectionLabel: z
                      .string()
                      .describe(
                        'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                      )
                      .max(20)
                      .optional()
                      .nullable(),
                    areaLabel: z
                      .string()
                      .describe(
                        'General admission or general seating area label.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                    tableLabel: z
                      .string()
                      .describe('Table label for events with table seating.')
                      .max(50)
                      .optional()
                      .nullable(),
                    rowLabel: z
                      .string()
                      .describe('Row label within a section.')
                      .max(50)
                      .optional()
                      .nullable(),
                    seatLabel: z
                      .string()
                      .describe(
                        'Individual seat label within a row or at a table.'
                      )
                      .max(4)
                      .optional()
                      .nullable(),
                    configurationId: z
                      .string()
                      .describe(
                        'Seating plan ID from the Wix Events & Tickets app.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Detailed seat location information.\n\nIncludes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.'
                  )
                  .optional(),
                guestPrice: z
                  .string()
                  .describe(
                    'The guest enters the ticket price. This pricing method is applied to donation.'
                  )
                  .optional()
                  .nullable(),
                pricingOptionId: z
                  .string()
                  .describe(
                    'ID of the pricing option selected for this ticket.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                pricingOptionName: z
                  .string()
                  .describe('Display name of the selected pricing option.')
                  .max(200)
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .object({
                    orderId: z
                      .string()
                      .describe(
                        'Order ID of the pricing plan purchase.\n\nReferences the specific order through which the customer acquired their subscription plan benefits.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    planId: z
                      .string()
                      .describe(
                        'Unique identifier of the pricing plan.\n\nReferences the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                  .describe(
                    'Pricing plan benefits applied to this ticket.\n\nContains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.'
                  )
                  .optional(),
                priceOverride: z
                  .string()
                  .describe(
                    'Override price that replaces the standard ticket price.\n\nUsed for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Detailed information about the tickets in this line item.'
              )
              .optional(),
            removedQuantity: z
              .number()
              .int()
              .describe(
                'Number of tickets that are removed from this line item after manually updating reservation in the dashboard.'
              )
              .min(0)
              .max(50)
              .optional()
              .nullable(),
            policy: z
              .string()
              .describe('Terms and conditions that apply to this ticket type.')
              .min(1)
              .max(50000)
              .optional()
              .nullable(),
            ticketName: z
              .string()
              .describe('Display name of the ticket type for this line item.')
              .min(1)
              .max(30)
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Price per individual ticket in this line item.\n\nRepresents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.'
              )
              .optional(),
            subTotal: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Total price for all tickets in this line item.\n\nCalculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      additionalReservationSettings: z
        .object({
          disableTicketLimitValidation: z
            .boolean()
            .describe(
              'Whether to bypass ticket availability limits when creating the reservation.\n\nWhen `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.'
            )
            .optional()
            .nullable(),
          enableHiddenTicketReservation: z
            .boolean()
            .describe(
              'Whether to allow reservations for tickets marked as hidden.\n\nHidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Advanced configuration options that modify default reservation behavior.\n\nUse these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extended fields allow you to add custom properties beyond the standard reservation data.\n\nLearn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allow you to classify ticket reservations.\n\nLearn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe(
      'Ticket reservation details to create.\n\nMust include at least one ticket line item with valid ticket definition ID\nand quantity. Additional settings like seat assignments and pricing options\ncan be specified in the ticket info.'
    ),
});
export const CreateTicketReservationResponse = z.object({
  _id: z
    .string()
    .describe('Ticket reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ticket reservation was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ticket reservation was updated.')
    .optional()
    .nullable(),
  expirationDate: z
    .date()
    .describe(
      "Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.\n\nWhen a reservation expires, tickets are released back to the available inventory.\nExpiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.\n\n**Note:** This field only applies to `PENDING` reservations."
    )
    .optional()
    .nullable(),
  status: z
    .enum(['PENDING', 'CONFIRMED', 'CANCELED', 'CANCELED_MANUALLY', 'EXPIRED'])
    .describe('Reservation status.')
    .optional(),
  memberId: z
    .string()
    .describe(
      'ID of the site member who created this reservation.\n\nOnly returned when the reservation was created by a logged-in site member.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  visitorId: z
    .string()
    .describe(
      'ID of the site visitor who created this reservation.\n\nReturned for both logged-in members and anonymous visitors.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  tickets: z
    .array(
      z.object({
        ticketLineItemId: z
          .string()
          .describe('Ticket line item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        eventId: z
          .string()
          .describe('ID of the event these tickets are for.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        ticketDefinitionId: z
          .string()
          .describe(
            'ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe('Number of tickets to reserve for this line item.')
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        ticketInfo: z
          .object({
            seatId: z
              .string()
              .describe(
                'Unique identifier for the assigned seat at the event venue.\n\nRequired for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            seatInfo: z
              .object({
                sectionLabel: z
                  .string()
                  .describe(
                    'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
                areaLabel: z
                  .string()
                  .describe('General admission or general seating area label.')
                  .max(50)
                  .optional()
                  .nullable(),
                tableLabel: z
                  .string()
                  .describe('Table label for events with table seating.')
                  .max(50)
                  .optional()
                  .nullable(),
                rowLabel: z
                  .string()
                  .describe('Row label within a section.')
                  .max(50)
                  .optional()
                  .nullable(),
                seatLabel: z
                  .string()
                  .describe('Individual seat label within a row or at a table.')
                  .max(4)
                  .optional()
                  .nullable(),
                configurationId: z
                  .string()
                  .describe(
                    'Seating plan ID from the Wix Events & Tickets app.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Detailed seat location information.\n\nIncludes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.'
              )
              .optional(),
            guestPrice: z
              .string()
              .describe(
                'The guest enters the ticket price. This pricing method is applied to donation.'
              )
              .optional()
              .nullable(),
            pricingOptionId: z
              .string()
              .describe('ID of the pricing option selected for this ticket.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            pricingOptionName: z
              .string()
              .describe('Display name of the selected pricing option.')
              .max(200)
              .optional()
              .nullable(),
            pricingPlan: z
              .object({
                orderId: z
                  .string()
                  .describe(
                    'Order ID of the pricing plan purchase.\n\nReferences the specific order through which the customer acquired their subscription plan benefits.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                planId: z
                  .string()
                  .describe(
                    'Unique identifier of the pricing plan.\n\nReferences the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Pricing plan benefits applied to this ticket.\n\nContains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.'
              )
              .optional(),
            priceOverride: z
              .string()
              .describe(
                'Override price that replaces the standard ticket price.\n\nUsed for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.'
              )
              .optional()
              .nullable(),
          })
          .describe('Detailed information about the tickets in this line item.')
          .optional(),
        removedQuantity: z
          .number()
          .int()
          .describe(
            'Number of tickets that are removed from this line item after manually updating reservation in the dashboard.'
          )
          .min(0)
          .max(50)
          .optional()
          .nullable(),
        policy: z
          .string()
          .describe('Terms and conditions that apply to this ticket type.')
          .min(1)
          .max(50000)
          .optional()
          .nullable(),
        ticketName: z
          .string()
          .describe('Display name of the ticket type for this line item.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        price: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
              )
              .optional(),
          })
          .describe(
            'Price per individual ticket in this line item.\n\nRepresents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.'
          )
          .optional(),
        subTotal: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
              )
              .optional(),
          })
          .describe(
            'Total price for all tickets in this line item.\n\nCalculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(50)
    .optional(),
  additionalReservationSettings: z
    .object({
      disableTicketLimitValidation: z
        .boolean()
        .describe(
          'Whether to bypass ticket availability limits when creating the reservation.\n\nWhen `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.'
        )
        .optional()
        .nullable(),
      enableHiddenTicketReservation: z
        .boolean()
        .describe(
          'Whether to allow reservations for tickets marked as hidden.\n\nHidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Advanced configuration options that modify default reservation behavior.\n\nUse these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extended fields allow you to add custom properties beyond the standard reservation data.\n\nLearn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags allow you to classify ticket reservations.\n\nLearn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).'
    )
    .optional(),
});
export const GetTicketReservationRequest = z.object({
  ticketReservationId: z
    .string()
    .describe('ID of the ticket reservation to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetTicketReservationResponse = z.object({
  _id: z
    .string()
    .describe('Ticket reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ticket reservation was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ticket reservation was updated.')
    .optional()
    .nullable(),
  expirationDate: z
    .date()
    .describe(
      "Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.\n\nWhen a reservation expires, tickets are released back to the available inventory.\nExpiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.\n\n**Note:** This field only applies to `PENDING` reservations."
    )
    .optional()
    .nullable(),
  status: z
    .enum(['PENDING', 'CONFIRMED', 'CANCELED', 'CANCELED_MANUALLY', 'EXPIRED'])
    .describe('Reservation status.')
    .optional(),
  memberId: z
    .string()
    .describe(
      'ID of the site member who created this reservation.\n\nOnly returned when the reservation was created by a logged-in site member.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  visitorId: z
    .string()
    .describe(
      'ID of the site visitor who created this reservation.\n\nReturned for both logged-in members and anonymous visitors.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  tickets: z
    .array(
      z.object({
        ticketLineItemId: z
          .string()
          .describe('Ticket line item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        eventId: z
          .string()
          .describe('ID of the event these tickets are for.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        ticketDefinitionId: z
          .string()
          .describe(
            'ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe('Number of tickets to reserve for this line item.')
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        ticketInfo: z
          .object({
            seatId: z
              .string()
              .describe(
                'Unique identifier for the assigned seat at the event venue.\n\nRequired for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            seatInfo: z
              .object({
                sectionLabel: z
                  .string()
                  .describe(
                    'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
                areaLabel: z
                  .string()
                  .describe('General admission or general seating area label.')
                  .max(50)
                  .optional()
                  .nullable(),
                tableLabel: z
                  .string()
                  .describe('Table label for events with table seating.')
                  .max(50)
                  .optional()
                  .nullable(),
                rowLabel: z
                  .string()
                  .describe('Row label within a section.')
                  .max(50)
                  .optional()
                  .nullable(),
                seatLabel: z
                  .string()
                  .describe('Individual seat label within a row or at a table.')
                  .max(4)
                  .optional()
                  .nullable(),
                configurationId: z
                  .string()
                  .describe(
                    'Seating plan ID from the Wix Events & Tickets app.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Detailed seat location information.\n\nIncludes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.'
              )
              .optional(),
            guestPrice: z
              .string()
              .describe(
                'The guest enters the ticket price. This pricing method is applied to donation.'
              )
              .optional()
              .nullable(),
            pricingOptionId: z
              .string()
              .describe('ID of the pricing option selected for this ticket.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            pricingOptionName: z
              .string()
              .describe('Display name of the selected pricing option.')
              .max(200)
              .optional()
              .nullable(),
            pricingPlan: z
              .object({
                orderId: z
                  .string()
                  .describe(
                    'Order ID of the pricing plan purchase.\n\nReferences the specific order through which the customer acquired their subscription plan benefits.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                planId: z
                  .string()
                  .describe(
                    'Unique identifier of the pricing plan.\n\nReferences the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Pricing plan benefits applied to this ticket.\n\nContains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.'
              )
              .optional(),
            priceOverride: z
              .string()
              .describe(
                'Override price that replaces the standard ticket price.\n\nUsed for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.'
              )
              .optional()
              .nullable(),
          })
          .describe('Detailed information about the tickets in this line item.')
          .optional(),
        removedQuantity: z
          .number()
          .int()
          .describe(
            'Number of tickets that are removed from this line item after manually updating reservation in the dashboard.'
          )
          .min(0)
          .max(50)
          .optional()
          .nullable(),
        policy: z
          .string()
          .describe('Terms and conditions that apply to this ticket type.')
          .min(1)
          .max(50000)
          .optional()
          .nullable(),
        ticketName: z
          .string()
          .describe('Display name of the ticket type for this line item.')
          .min(1)
          .max(30)
          .optional()
          .nullable(),
        price: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
              )
              .optional(),
          })
          .describe(
            'Price per individual ticket in this line item.\n\nRepresents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.'
          )
          .optional(),
        subTotal: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
              )
              .optional(),
          })
          .describe(
            'Total price for all tickets in this line item.\n\nCalculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(50)
    .optional(),
  additionalReservationSettings: z
    .object({
      disableTicketLimitValidation: z
        .boolean()
        .describe(
          'Whether to bypass ticket availability limits when creating the reservation.\n\nWhen `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.'
        )
        .optional()
        .nullable(),
      enableHiddenTicketReservation: z
        .boolean()
        .describe(
          'Whether to allow reservations for tickets marked as hidden.\n\nHidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Advanced configuration options that modify default reservation behavior.\n\nUse these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Extended fields allow you to add custom properties beyond the standard reservation data.\n\nLearn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags allow you to classify ticket reservations.\n\nLearn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).'
    )
    .optional(),
});
export const DeleteTicketReservationRequest = z.object({
  ticketReservationId: z
    .string()
    .describe('ID of the ticket reservation to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteTicketReservationResponse = z.object({
  ticketReservation: z
    .object({
      _id: z
        .string()
        .describe('Ticket reservation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ticket reservation was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ticket reservation was updated.')
        .optional()
        .nullable(),
      expirationDate: z
        .date()
        .describe(
          "Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.\n\nWhen a reservation expires, tickets are released back to the available inventory.\nExpiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.\n\n**Note:** This field only applies to `PENDING` reservations."
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'PENDING',
          'CONFIRMED',
          'CANCELED',
          'CANCELED_MANUALLY',
          'EXPIRED',
        ])
        .describe('Reservation status.')
        .optional(),
      memberId: z
        .string()
        .describe(
          'ID of the site member who created this reservation.\n\nOnly returned when the reservation was created by a logged-in site member.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      visitorId: z
        .string()
        .describe(
          'ID of the site visitor who created this reservation.\n\nReturned for both logged-in members and anonymous visitors.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      tickets: z
        .array(
          z.object({
            ticketLineItemId: z
              .string()
              .describe('Ticket line item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            eventId: z
              .string()
              .describe('ID of the event these tickets are for.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            ticketDefinitionId: z
              .string()
              .describe(
                'ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe('Number of tickets to reserve for this line item.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            ticketInfo: z
              .object({
                seatId: z
                  .string()
                  .describe(
                    'Unique identifier for the assigned seat at the event venue.\n\nRequired for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                seatInfo: z
                  .object({
                    sectionLabel: z
                      .string()
                      .describe(
                        'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                      )
                      .max(20)
                      .optional()
                      .nullable(),
                    areaLabel: z
                      .string()
                      .describe(
                        'General admission or general seating area label.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                    tableLabel: z
                      .string()
                      .describe('Table label for events with table seating.')
                      .max(50)
                      .optional()
                      .nullable(),
                    rowLabel: z
                      .string()
                      .describe('Row label within a section.')
                      .max(50)
                      .optional()
                      .nullable(),
                    seatLabel: z
                      .string()
                      .describe(
                        'Individual seat label within a row or at a table.'
                      )
                      .max(4)
                      .optional()
                      .nullable(),
                    configurationId: z
                      .string()
                      .describe(
                        'Seating plan ID from the Wix Events & Tickets app.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Detailed seat location information.\n\nIncludes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.'
                  )
                  .optional(),
                guestPrice: z
                  .string()
                  .describe(
                    'The guest enters the ticket price. This pricing method is applied to donation.'
                  )
                  .optional()
                  .nullable(),
                pricingOptionId: z
                  .string()
                  .describe(
                    'ID of the pricing option selected for this ticket.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                pricingOptionName: z
                  .string()
                  .describe('Display name of the selected pricing option.')
                  .max(200)
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .object({
                    orderId: z
                      .string()
                      .describe(
                        'Order ID of the pricing plan purchase.\n\nReferences the specific order through which the customer acquired their subscription plan benefits.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    planId: z
                      .string()
                      .describe(
                        'Unique identifier of the pricing plan.\n\nReferences the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                  .describe(
                    'Pricing plan benefits applied to this ticket.\n\nContains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.'
                  )
                  .optional(),
                priceOverride: z
                  .string()
                  .describe(
                    'Override price that replaces the standard ticket price.\n\nUsed for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Detailed information about the tickets in this line item.'
              )
              .optional(),
            removedQuantity: z
              .number()
              .int()
              .describe(
                'Number of tickets that are removed from this line item after manually updating reservation in the dashboard.'
              )
              .min(0)
              .max(50)
              .optional()
              .nullable(),
            policy: z
              .string()
              .describe('Terms and conditions that apply to this ticket type.')
              .min(1)
              .max(50000)
              .optional()
              .nullable(),
            ticketName: z
              .string()
              .describe('Display name of the ticket type for this line item.')
              .min(1)
              .max(30)
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Price per individual ticket in this line item.\n\nRepresents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.'
              )
              .optional(),
            subTotal: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Total price for all tickets in this line item.\n\nCalculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      additionalReservationSettings: z
        .object({
          disableTicketLimitValidation: z
            .boolean()
            .describe(
              'Whether to bypass ticket availability limits when creating the reservation.\n\nWhen `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.'
            )
            .optional()
            .nullable(),
          enableHiddenTicketReservation: z
            .boolean()
            .describe(
              'Whether to allow reservations for tickets marked as hidden.\n\nHidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Advanced configuration options that modify default reservation behavior.\n\nUse these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extended fields allow you to add custom properties beyond the standard reservation data.\n\nLearn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allow you to classify ticket reservations.\n\nLearn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('Deleted ticket reservation.')
    .optional(),
});
export const BulkUpdateTicketReservationTagsRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to add to the specified reservations.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to remove from the specified reservations.')
      .optional(),
  }),
});
export const BulkUpdateTicketReservationTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual reservation update operation.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata about the bulk update operation.')
    .optional(),
});
export const BulkUpdateTicketReservationTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter criteria to select reservations for tag updates.'),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to add to matching reservations.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to remove from matching reservations.')
      .optional(),
  }),
});
export const BulkUpdateTicketReservationTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID for tracking the asynchronous operation.\n\nUse this ID to monitor the progress and status of the bulk tag update operation.\nThe operation runs in the background and may take time to complete for large datasets.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const CancelTicketReservationRequest = z.object({
  ticketReservationId: z
    .string()
    .describe('ID of the ticket reservation to cancel.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CancelTicketReservationResponse = z.object({
  ticketReservation: z
    .object({
      _id: z
        .string()
        .describe('Ticket reservation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ticket reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the ticket reservation.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ticket reservation was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ticket reservation was updated.')
        .optional()
        .nullable(),
      expirationDate: z
        .date()
        .describe(
          "Date and time when a `PENDING` ticket reservation will automatically expire if not confirmed.\n\nWhen a reservation expires, tickets are released back to the available inventory.\nExpiration time is calculated based on the event's reservation duration settings. To set the reservation duration, call [Create Event](https://dev.wix.com/docs/api-reference/business-solutions/events/events-v3/create-event) API.\n\n**Note:** This field only applies to `PENDING` reservations."
        )
        .optional()
        .nullable(),
      status: z
        .enum([
          'PENDING',
          'CONFIRMED',
          'CANCELED',
          'CANCELED_MANUALLY',
          'EXPIRED',
        ])
        .describe('Reservation status.')
        .optional(),
      memberId: z
        .string()
        .describe(
          'ID of the site member who created this reservation.\n\nOnly returned when the reservation was created by a logged-in site member.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      visitorId: z
        .string()
        .describe(
          'ID of the site visitor who created this reservation.\n\nReturned for both logged-in members and anonymous visitors.\n\nLearn more about [identities](https://dev.wix.com/docs/api-reference/articles/get-started/about-identities).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      tickets: z
        .array(
          z.object({
            ticketLineItemId: z
              .string()
              .describe('Ticket line item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            eventId: z
              .string()
              .describe('ID of the event these tickets are for.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            ticketDefinitionId: z
              .string()
              .describe(
                'ID of the [ticket definition](https://dev.wix.com/docs/api-reference/business-solutions/events/ticket-definitions-v3/introduction) being reserved.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe('Number of tickets to reserve for this line item.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            ticketInfo: z
              .object({
                seatId: z
                  .string()
                  .describe(
                    'Unique identifier for the assigned seat at the event venue.\n\nRequired for events with assigned seating. Each ticket in the line item can have a different seat assignment within the same section or area.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                seatInfo: z
                  .object({
                    sectionLabel: z
                      .string()
                      .describe(
                        'High-level division of the venue, where the place is located, such as `Orchestra`, `Balcony`, or `VIP Section`.'
                      )
                      .max(20)
                      .optional()
                      .nullable(),
                    areaLabel: z
                      .string()
                      .describe(
                        'General admission or general seating area label.'
                      )
                      .max(50)
                      .optional()
                      .nullable(),
                    tableLabel: z
                      .string()
                      .describe('Table label for events with table seating.')
                      .max(50)
                      .optional()
                      .nullable(),
                    rowLabel: z
                      .string()
                      .describe('Row label within a section.')
                      .max(50)
                      .optional()
                      .nullable(),
                    seatLabel: z
                      .string()
                      .describe(
                        'Individual seat label within a row or at a table.'
                      )
                      .max(4)
                      .optional()
                      .nullable(),
                    configurationId: z
                      .string()
                      .describe(
                        'Seating plan ID from the Wix Events & Tickets app.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Detailed seat location information.\n\nIncludes section, row, seat number, and other venue-specific identifiers to help customers locate their assigned seats.'
                  )
                  .optional(),
                guestPrice: z
                  .string()
                  .describe(
                    'The guest enters the ticket price. This pricing method is applied to donation.'
                  )
                  .optional()
                  .nullable(),
                pricingOptionId: z
                  .string()
                  .describe(
                    'ID of the pricing option selected for this ticket.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                pricingOptionName: z
                  .string()
                  .describe('Display name of the selected pricing option.')
                  .max(200)
                  .optional()
                  .nullable(),
                pricingPlan: z
                  .object({
                    orderId: z
                      .string()
                      .describe(
                        'Order ID of the pricing plan purchase.\n\nReferences the specific order through which the customer acquired their subscription plan benefits.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    planId: z
                      .string()
                      .describe(
                        'Unique identifier of the pricing plan.\n\nReferences the specific subscription plan that grants pricing benefits or discounts for this ticket reservation.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                  .describe(
                    'Pricing plan benefits applied to this ticket.\n\nContains information about subscription plan discounts or benefits that affect the ticket pricing for this customer.'
                  )
                  .optional(),
                priceOverride: z
                  .string()
                  .describe(
                    'Override price that replaces the standard ticket price.\n\nUsed for promotional pricing, staff discounts, or other special pricing that differs from the standard ticket definition price.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Detailed information about the tickets in this line item.'
              )
              .optional(),
            removedQuantity: z
              .number()
              .int()
              .describe(
                'Number of tickets that are removed from this line item after manually updating reservation in the dashboard.'
              )
              .min(0)
              .max(50)
              .optional()
              .nullable(),
            policy: z
              .string()
              .describe('Terms and conditions that apply to this ticket type.')
              .min(1)
              .max(50000)
              .optional()
              .nullable(),
            ticketName: z
              .string()
              .describe('Display name of the ticket type for this line item.')
              .min(1)
              .max(30)
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Price per individual ticket in this line item.\n\nRepresents the base price for one ticket before any quantity calculations. This price includes any pricing option discounts but excludes service fees.'
              )
              .optional(),
            subTotal: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount. Decimal string with a period as a decimal separator (for example, 3.99). Optionally, a single (-), to indicate that the amount is negative.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Currency code. Must be a valid [ISO 4217](https://www.iso.org/iso-4217-currency-codes.html) currency code (for example, USD).'
                  )
                  .optional(),
              })
              .describe(
                'Total price for all tickets in this line item.\n\nCalculated as `price` multiplied by `quantity`. This represents the subtotal for this line item before taxes and service fees are applied.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      additionalReservationSettings: z
        .object({
          disableTicketLimitValidation: z
            .boolean()
            .describe(
              'Whether to bypass ticket availability limits when creating the reservation.\n\nWhen `true`, allows creating reservations even if tickets appear sold out. Use with caution as this can lead to overselling. Typically used for admin purposes or special circumstances.'
            )
            .optional()
            .nullable(),
          enableHiddenTicketReservation: z
            .boolean()
            .describe(
              'Whether to allow reservations for tickets marked as hidden.\n\nHidden tickets are not displayed in the standard ticket selection on the site. Enable this to allow reservations for hidden tickets through direct API calls.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Advanced configuration options that modify default reservation behavior.\n\nUse these settings to override standard validation rules, such as availability limits or hidden ticket restrictions.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Extended fields allow you to add custom properties beyond the standard reservation data.\n\nLearn more about [extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions).'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags allow you to classify ticket reservations.\n\nLearn more about [tags](https://dev.wix.com/docs/rest/business-management/tags/introduction).'
        )
        .optional(),
    })
    .describe('The canceled ticket reservation.')
    .optional(),
});
